import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, History, Loader2, Trophy } from 'lucide-react';
import '../components/wb/wb.css';
import { useWbGame } from '../components/wb/useWbGame';
import { wbSfx, preloadSfx } from '../components/wb/wbSfx';
import { music, getAudio } from '../components/wb/wbMusic';
import { WbAudioMenu } from '../components/wb/WbAudioMenu';
import { WbTutorial } from '../components/wb/WbTutorial';
import { WbScene } from '../components/wb/WbScene';
import { WbHud } from '../components/wb/WbHud';
import { WbWallet } from '../components/wb/WbWallet';
import { WbFeed } from '../components/wb/WbFeed';
import { WbPopResult } from '../components/wb/WbPopResult';
import { WbRankings } from '../components/wb/WbRankings';
import { WbMyPops } from '../components/wb/WbMyPops';
import { WbRules } from '../components/wb/WbRules';
import { WbGoldenRain } from '../components/wb/WbGoldenRain';

const TAB_MUSIC = { inicio: '/wb/music_vila.mp3', ranking: '/wb/music_ranking.mp3', meus: '/wb/music_doces.mp3' };
const TABS = [
  { id: 'inicio', label: 'BALÃO', Icon: Flame },
  { id: 'ranking', label: 'RANKING', Icon: Trophy },
  { id: 'meus', label: 'MEUS BALÕES', Icon: History },
];
const QUIET = { value: 0, pulseId: 0 };

const Splash = ({ onEnter, loading }) => (
  <div className="fixed inset-0 z-[60] flex flex-col items-center justify-end overflow-hidden bg-[#0a0612]" data-testid="wb-splash">
    <img src="/wb/bg.webp" alt="" className="absolute inset-0 h-full w-full object-cover object-top" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0612] via-[#0a0612]/40 to-transparent" />
    <img src="/wb/witch_idle.webp" alt="" className="absolute bottom-[26%] left-1/2 h-[42%] -translate-x-1/2 drop-shadow-[0_20px_30px_rgba(0,0,0,.7)]" style={{ animation: 'wb-bob 3s ease-in-out infinite' }} />
    <div className="relative z-10 mb-14 w-full max-w-md px-6 text-center">
      <h1 className="wb-title text-5xl text-orange-400 sm:text-6xl" style={{ textShadow: '0 0 30px rgba(249,115,22,.8), 0 4px 0 #3b0764' }}>WITCH'S BALLOON</h1>
      <p className="mt-2 text-[11px] font-black uppercase tracking-[.3em] text-purple-200/90">recarregue · junte moedas da bruxa · estoure balões</p>
      <button onClick={onEnter} disabled={loading} className="wb-btn-collect mt-6 disabled:opacity-70" data-testid="wb-enter-btn">
        {loading ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Entrar na vila'}
      </button>
    </div>
  </div>
);

export default function WitchBalloonPage() {
  const navigate = useNavigate();
  const { state, loading, erro, feed, rankTick, goldenWin, clearGolden, pop } = useWbGame();
  const [tab, setTab] = useState('inicio');
  const [now, setNow] = useState(Date.now());
  const [booted, setBooted] = useState(false);
  const [audio, setAudio] = useState(getAudio());
  const [audioOpen, setAudioOpen] = useState(false);
  const [rules, setRules] = useState(false);
  const [tutorial, setTutorial] = useState(false);
  const [pressure, setPressure] = useState(QUIET);
  const [explosion, setExplosion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [popErro, setPopErro] = useState('');
  const loadedAt = useRef(Date.now());
  const pending = useRef(null);

  useEffect(() => { if (!localStorage.getItem('customer_token')) navigate('/conta'); }, [navigate]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { loadedAt.current = Date.now(); }, [state]);
  useEffect(() => { if (booted && state?.event) music.play(TAB_MUSIC[tab]); }, [tab, booted, state?.event]);
  useEffect(() => () => music.stop(), []);

  const enter = () => {
    setBooted(true); wbSfx.whoosh(); preloadSfx();
    if (!localStorage.getItem('wb_tutorial_seen')) setTutorial(true);
  };
  const fecharTutorial = () => { setTutorial(false); localStorage.setItem('wb_tutorial_seen', '1'); };
  const goRecharge = useCallback(() => navigate('/'), [navigate]);

  /* Estourar: pede ao servidor (ele decide) enquanto a bruxa aperta o detonador; o BOOM revela o resultado. */
  const estourar = useCallback(async () => {
    if (busy) return;
    setBusy(true); setPopErro(''); setResult(null); setTab('inicio');
    wbSfx.pump(70, 70);
    setPressure({ value: 70, peak: 78, pulseId: Date.now() });
    const r = await pop();
    if (!r.ok) {
      setBusy(false); setPressure((q) => ({ ...q, value: 0, peak: 0 })); setPopErro(r.message || 'Não deu para estourar agora.');
      return;
    }
    pending.current = r.pop;
    music.duck(4500);
    setExplosion({ id: r.pop.pop_id, candies: { bala: 6, abobora: 4, pirulito: 4, caveira: 3 }, bags: Number(r.pop.prize) > 0 ? 1 : 0 });
  }, [busy, pop]);

  const onBoomDone = useCallback(() => {
    setExplosion(null); setPressure((q) => ({ ...q, value: 0, peak: 0 })); setBusy(false);
    if (pending.current) { setResult(pending.current); pending.current = null; }
  }, []);

  if (!booted || (loading && !state)) return <Splash onEnter={enter} loading={loading && !state} />;

  if (!state?.event) {
    return (
      <div className="wb-root flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="wb-no-event">
        <img src="/wb/witch_scared.webp" alt="" className="h-48 object-contain" />
        <h1 className="wb-title mt-4 text-4xl text-orange-400">WITCH'S BALLOON</h1>
        {!erro && (
          <p className="mt-3 inline-block rounded-full border border-rose-400/70 bg-rose-950/60 px-4 py-1 text-xs font-black uppercase tracking-[0.2em] text-rose-300" data-testid="wb-inactive-badge">
            Evento inativo
          </p>
        )}
        <p className="mt-3 text-sm text-purple-200/80" data-testid="wb-inactive-msg">
          {erro || <>O WITCH'S BALLOON não está disponível no momento.<br />A bruxa avisa quando os balões voltarem a subir! 🎃</>}
        </p>
        <button onClick={() => navigate('/conta')} className="mt-6 rounded-full border border-purple-400/60 px-6 py-2 text-xs font-black text-purple-200" data-testid="wb-no-event-back">← Voltar à minha conta</button>
      </div>
    );
  }

  const { event, me, config } = state;
  const elapsed = Math.floor((now - loadedAt.current) / 1000);
  const secondsLeft = Math.max(0, (state.seconds_left || 0) - elapsed);
  const secondsToStart = Math.max(0, (state.seconds_to_start || 0) - elapsed);
  const live = state.live && secondsLeft > 0 && secondsToStart === 0;
  const paused = event.status === 'PAUSED';
  const ended = event.status === 'ENDED' || secondsLeft === 0;

  return (
    <div className="wb-root relative min-h-screen" data-testid="wb-page">
      <div className="pointer-events-none fixed inset-0 z-0 hidden md:block" aria-hidden="true">
        <img src="/wb/bg.webp" alt="" className="h-full w-full scale-110 object-cover opacity-25 blur-2xl" />
        <div className="absolute inset-0 bg-[#0a0612]/80" />
      </div>
      <div className="pointer-events-none fixed inset-y-0 left-1/2 z-[45] hidden w-full max-w-md -translate-x-1/2 border-x border-purple-900/60 shadow-[0_0_80px_rgba(0,0,0,.95)] md:block" aria-hidden="true" />

      <WbHud secondsLeft={secondsLeft} secondsToStart={secondsToStart} dayIndex={state.day_index} duration={state.duration_days} balance={me?.balance} onRecharge={goRecharge}
        sound={audio.musicOn || audio.sfxOn} onSound={() => setAudioOpen((o) => !o)} onExit={() => navigate('/conta')} onRules={() => setTutorial(true)} />
      {audioOpen && (
        <div className="fixed inset-0 z-[60]" onClick={() => setAudioOpen(false)} data-testid="wb-audio-backdrop">
          <div className="wb-audio-pop absolute right-3" onClick={(e) => e.stopPropagation()}>
            <WbAudioMenu onChange={setAudio} />
          </div>
        </div>
      )}

      <main className="relative z-10 mx-auto max-w-md space-y-3 px-3 pb-32 pt-2">
        {ended && (
          <div className="rounded-2xl border border-red-400/50 bg-red-950/50 px-4 py-2 text-center text-[11px] font-black uppercase tracking-widest text-red-200" data-testid="wb-ended-banner">
            evento encerrado · ranking final abaixo
          </div>
        )}
        {erro && <p className="rounded-2xl bg-red-950/50 px-4 py-2 text-center text-[11px] font-bold text-red-200" data-testid="wb-error">{erro}</p>}

        {tab === 'inicio' && (
          <>
            <WbScene pressure={pressure} explosion={explosion} onExplosionDone={onBoomDone} live={live} paused={paused}>
              <WbGoldenRain win={goldenWin} onDone={clearGolden} />
            </WbScene>
            {me && <WbWallet me={me} config={config} busy={busy} live={live} onPop={estourar} onRecharge={goRecharge} />}
            {popErro && <p className="rounded-2xl bg-red-950/50 px-4 py-2 text-center text-[11px] font-bold text-red-200" data-testid="wb-pop-error">{popErro}</p>}
            <WbFeed feed={feed} />
          </>
        )}
        {tab === 'ranking' && <WbRankings tick={rankTick} me={me} config={config} />}
        {tab === 'meus' && <WbMyPops me={me} tick={rankTick} />}
      </main>

      {result && !tutorial && !rules && (
        <WbPopResult pop={result} balance={me?.balance} popCost={config.pop_cost} onClose={() => setResult(null)}
          onAgain={() => { setResult(null); estourar(); }} onRecharge={goRecharge} />
      )}
      {tutorial && <WbTutorial onClose={fecharTutorial} onRules={() => setRules(true)} />}
      {rules && <WbRules config={config} onClose={() => setRules(false)} />}

      <nav className="pwa-safe-bottom fixed bottom-0 left-1/2 z-50 w-full max-w-md -translate-x-1/2 border-t border-purple-900/70 bg-[#0a0612]/95 backdrop-blur" data-testid="wb-nav">
        <div className="flex">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { if (t.id !== tab) wbSfx.click(); setTab(t.id); }} data-testid={`wb-tab-${t.id}`}
              className={`flex flex-1 flex-col items-center gap-1 py-2.5 transition-colors ${tab === t.id ? 'text-orange-400' : 'text-purple-300/50'}`}>
              <t.Icon className={`h-6 w-6 ${tab === t.id ? 'drop-shadow-[0_0_8px_rgba(249,115,22,.8)]' : ''}`} />
              <span className="text-[9px] font-black tracking-wider">{t.label}</span>
              {tab === t.id && <span className="h-0.5 w-8 rounded-full bg-orange-500" />}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
